/**
 * Chunked upload клиенты (ADR-020, TM-a171ece6).
 *
 * Контракт см. `src/recognition/api/routers/uploads.py`. Используется
 * только когда файл > LEGACY_UPLOAD_THRESHOLD (см. useUpload.ts).
 */

import { apiFetch } from "@/api/client";
import { ApiError } from "@/api/errors";
import type {
  MeetingSummary,
  RecognitionErrorEnvelope,
  UUID,
  UploadCompleteRequest,
  UploadInitRequest,
  UploadInitResponse,
  UploadStateResponse,
} from "@/api/types";

export async function initUpload(
  payload: UploadInitRequest,
): Promise<UploadInitResponse> {
  return apiFetch<UploadInitResponse>("/api/uploads/init", {
    method: "POST",
    body: payload,
  });
}

export async function getUploadState(
  uploadId: UUID,
): Promise<UploadStateResponse> {
  return apiFetch<UploadStateResponse>(`/api/uploads/${uploadId}`, {
    method: "GET",
  });
}

export async function completeUpload(
  uploadId: UUID,
  payload: UploadCompleteRequest,
): Promise<MeetingSummary> {
  return apiFetch<MeetingSummary>(`/api/uploads/${uploadId}/complete`, {
    method: "POST",
    body: payload,
  });
}

/**
 * POST один chunk через нативный fetch (apiFetch заточен на JSON, а тут
 * raw bytes). Возвращает разрешённый Promise при 204; при ошибке —
 * Error с сообщением + status.
 *
 * Поддерживает AbortSignal — если клиент решил отменить, fetch
 * прерывается с AbortError.
 */
export async function uploadChunk(
  uploadId: UUID,
  offset: number,
  body: Blob,
  signal?: AbortSignal,
): Promise<void> {
  const resp = await fetch(`/api/uploads/${uploadId}/chunk?offset=${offset}`, {
    method: "POST",
    body,
    credentials: "include",
    signal,
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    // The chunk endpoint bypasses apiFetch, so the domain envelope has to be
    // unpacked here — otherwise every failure reaches the UI as a raw
    // "chunk upload failed (status=507): {...json...}" blob and `describeError`
    // has no `code` to translate (TM-fb963707).
    let envelope: Partial<RecognitionErrorEnvelope> = {};
    try {
      envelope = JSON.parse(text) as Partial<RecognitionErrorEnvelope>;
    } catch {
      envelope = {};
    }
    if (typeof envelope.code === "string" && envelope.code.length > 0) {
      throw new ApiError({
        code: envelope.code,
        message: typeof envelope.message === "string" ? envelope.message : text,
        status: resp.status,
        details: envelope.details ?? {},
      });
    }
    throw new Error(
      `chunk upload failed (status=${resp.status}, offset=${offset}): ${text.slice(0, 200)}`,
    );
  }
}
